import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import axios from 'src/axios';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  Tooltip,
  Typography,
  Alert,
  IconButton
} from '@mui/material';
import { APICONSTANTS, CONSTANTS, PEDIGREE } from 'src/components/Constants';
import { SnackContext, ThemeContext } from 'src/store/ContextStore';
import { getPersonAge, calculateAgeOnDate } from 'src/util/util';
import { toLocalDate } from 'src/util/partialDate';
import {
  recordedAs,
  searchDiseases,
  storedDiseaseFor
} from 'src/util/diseaseSearch';
import DiagnosisOptionRow from 'src/components/settings/DiagnosisOptionRow';
import DiseaseCodeChoice from 'src/components/settings/DiseaseCodeChoice';
import { AppContext } from 'src/store/ContextStore';
import { styled } from '@mui/material/styles';
import CustomDialog, {
  CLINICAL_RECORD_DIALOG_WIDTH
} from 'src/components/UI/dialog/CustomDialog';
import EditDiagnoseModal from './EditDiagnoseModel';
import UnsavedChangesDialog from 'src/components/UI/dialog/UnsavedChangesDialog';
import { EVENTS, isDiseaseId } from 'src/components/admin/appReducer';
import { useRecordOpenRequest } from '../clinical-notes-section/useRecordOpenRequest';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import DeleteModal from 'src/components/proband-info/DeleteModal';
import dayjs from 'dayjs';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { useLoggedInUserName } from 'src/hooks/useLoggedInUserName';
import { useStaffMemberNames } from 'src/hooks/useStaffMemberNames';
import HistoryIcon from '@mui/icons-material/History';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import ModalHeader from './ModalHeader';
import { formatPersonSummary } from '../../util';
import { TILE_COLOURS } from '../tileColours';
import { isAnonymousOrLoading } from '../../../anonymousPedigrees';
import { notYetIn, useRowRecordSync } from '../useRowRecordSync';
import { mergeSavedRecords } from '../../../recordRouteResponse';

// This section lists diseases only; genes and HPO terms have their own.
const isDiseaseRecord = (d) => isDiseaseId(d?.id);

const CardGrid = styled(Grid)({
  marginBottom: 5,
  cursor: 'pointer',
  borderRadius: 6,
  padding: '8px 10px',
  border: `1px solid ${TILE_COLOURS.disease.border}`,
  borderLeft: `3px solid ${TILE_COLOURS.disease.accent}`,
  backgroundColor: TILE_COLOURS.disease.background,
  display: 'flex',
  justifyContent: 'space-between',
  gap: 8
});

const DiagnoseName = styled(Typography)({
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
});

const RightIcon = styled(CheckCircleOutlineIcon)({
  color: 'green',
  fontSize: 20
});

const ClinicallyConfirmedContainer = styled(Grid)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
});

const LegendBox = styled(Grid)({
  display: 'flex',
  gap: 5
});

// Exported for its age-on-date test (recordAgeOnDate.inline.test.jsx).
export function InlineDiagnosisCapture({
  record,
  patientDob,
  maxAge,
  dateFormat,
  anonymous,
  onSaveCapture,
  onSkip
}) {
  const t = useAutoTranslation();

  const hasDob = Boolean(patientDob);
  // Always default to age input — clinicians typically have the age at the
  // time of recording, not the exact date. Users can flip to date mode via
  // the "Enter date instead" affordance below.
  const [inputMode, setInputMode] = useState('age');
  const [dateValue, setDateValue] = useState(null);
  const [ageValue, setAgeValue] = useState('');
  const [ageError, setAgeError] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const interactingRef = useRef(false);

  // Inline validation — block submission and surface the error in the
  // input itself so the user can correct without re-opening. The parent
  // onEditDiagnose still validates as a safety net.
  const validateAge = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = Number(value);
    if (Number.isNaN(num) || num < 0) return t('Age must be a positive number');
    if (typeof maxAge === 'number' && Number.isFinite(maxAge) && num > maxAge) {
      return t("Age cannot exceed the person's age ({{maxAge}}).", { maxAge });
    }
    return '';
  };
  // Mirror the controlled values in refs so handleSubmit reads the latest
  // input even when the browser delivers the trailing onChange and the Enter
  // keyDown in the same task tick — observed in Chrome where React 18
  // batching otherwise leaves the closure one render behind.
  const ageValueRef = useRef('');
  const dateValueRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus?.();
    }, 100);
    return () => clearTimeout(timer);
  }, [inputMode]);

  const handleSubmit = () => {
    if (saving) return;
    const liveDate = dateValueRef.current;
    const liveAge = ageValueRef.current;
    if (inputMode === 'date' && !liveDate) return onSkip();
    if (inputMode === 'age' && !liveAge) return onSkip();

    if (inputMode === 'age') {
      const err = validateAge(liveAge);
      if (err) {
        setAgeError(err);
        return;
      }
    }

    setSaving(true);
    const update = { ...record };
    if (inputMode === 'date') {
      update.dateReported = liveDate.format('YYYY-MM-DD');
      if (hasDob) {
        // The age on the calendar day just written, read as the calendar date
        // it is (toLocalDate); blank before the birth.
        update.age = String(
          calculateAgeOnDate(patientDob, update.dateReported, toLocalDate)
        );
      }
    } else {
      update.age = String(liveAge);
    }
    onSkip();
    onSaveCapture(update);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onSkip();
    }
  };

  const switchMode = () => {
    setDateValue(null);
    setAgeValue('');
    setAgeError('');
    dateValueRef.current = null;
    ageValueRef.current = '';
    setInputMode((prev) => (prev === 'date' ? 'age' : 'date'));
  };

  return (
    <Box
      sx={{
        px: 1.5,
        pb: 1.5,
        pt: 1,
        mb: 0.5,
        bgcolor: '#fafafa',
        border: '1px solid #ccc',
        borderTop: 'none',
        borderRadius: '0 0 5px 5px'
      }}
    >
      {inputMode === 'date' ? (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={t('Date of diagnosis')}
            format={dateFormat || 'DD/MM/YYYY'}
            value={dayjs.isDayjs(dateValue) ? dateValue : null}
            onChange={(val) => {
              dateValueRef.current = val;
              setDateValue(val);
            }}
            disableFuture
            minDate={patientDob ? dayjs(patientDob) : undefined}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                onKeyDown: handleKeyDown,
                inputRef
              }
            }}
          />
        </LocalizationProvider>
      ) : (
        <TextField
          label={t('Age at diagnosis')}
          type="number"
          size="small"
          fullWidth
          value={ageValue}
          onChange={(e) => {
            const v = e.target.value;
            ageValueRef.current = v;
            setAgeValue(v);
            setAgeError(validateAge(v));
          }}
          onKeyDown={handleKeyDown}
          inputRef={inputRef}
          error={!!ageError}
          helperText={ageError || undefined}
          slotProps={{
            htmlInput: { min: 0, max: maxAge }
          }}
        />
      )}
      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
        {/* anonymous pedigrees: capture is age-only — no date entry */}
        {!anonymous && (
          <Typography
            variant="caption"
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              '&:hover': { textDecoration: 'underline' }
            }}
            onClick={switchMode}
          >
            {inputMode === 'date'
              ? t('Enter age instead')
              : t('Enter date instead')}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{
            cursor: 'pointer',
            color: 'text.secondary',
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={onSkip}
        >
          {t('Skip')}
        </Typography>
      </Box>
    </Box>
  );
}

const DiagnosesMenu = ({
  data,
  setMemberRecords,
  memberRecords,
  setPedigreeState,
  modificationHistoryHandle,
  setLabelsSet,
  onDirtyChange,
  saveRef,
  discardRef,
  isDisabled,
  clinicalRefreshKey = 0
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchedDiagnoses, setFetchedDiagnoses] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDiagnose, setSelectedDiagnose] = useState({});
  // Whether the open disease editor has unsaved edits, and whether we are
  // currently asking about them. Held here rather than in EditDiagnoseModal
  // because the dialog's header X belongs to CustomDialog, not to the child.
  const [diagnoseDirty, setDiagnoseDirty] = useState(false);
  const [unsavedPrompt, setUnsavedPrompt] = useState(false);
  const [ageConfirmationModal, setAgeConfirmationModal] = useState({
    status: false,
    data: {}
  });
  // Reason-carrying. /terminology/network/search/disease answers 200 with an
  // empty list for an outage, an admin switch-off, an empty vocabulary and a
  // genuine no-match alike, and its three source legs run under allSettled so
  // a rejected leg silently became [] (TGCLOUD-1785).
  const [unavailable, setUnavailable] = useState(null);
  const [expandedNewIds, setExpandedNewIds] = useState(new Set());
  // Coding-system chooser: opened when a selected disease exists under more
  // than one vocabulary, so the clinician records the code they intend.
  const [codingChoice, setCodingChoice] = useState({
    open: false,
    option: null,
    selectedId: null
  });

  const staffMemberNames = useStaffMemberNames();

  const t = useAutoTranslation();

  const { setSnack } = useContext(SnackContext);
  const { content, dispatchUpdate } = useContext(AppContext);
  const { theme } = useContext(ThemeContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const anonymous = isAnonymousOrLoading(content?.settings?.pedigreeSetting);
  // Who is creating the record — the signed-in clinician, not the proband
  // this chart belongs to.
  const currentUserName = useLoggedInUserName();

  let user_id = useMemo(() => {
    return user?.user_id || user?._id;
  }, [user]);

  // Id for family member
  let familyMemberId = useMemo(() => {
    return data?.familyMemberId;
  }, [data]);

  // Id for proband
  let proBandId = useMemo(() => {
    return data?.proBandId;
  }, [data]);

  // Notify parent of dirty state (unsaved selected items)
  useEffect(() => {
    onDirtyChange?.(selectedItems.length > 0);
  }, [selectedItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (discardRef)
      discardRef.current = () => {
        setSelectedItems([]);
        setSearchTerm('');
      };
  });

  // One page of the disease search (util/diseaseSearch, TGCLOUD-1892): the
  // backend sends no second page, so the scroll that waited for one is gone.
  // An answer for words no longer in the box is dropped.
  const searchSeq = useRef(0);
  const fetchDataByDisease = async () => {
    const seq = ++searchSeq.current;
    if (searchTerm.length < 3) {
      setOptions([]);
      return;
    }
    const { options: found, unavailable: why } = await searchDiseases(
      searchTerm
    );
    if (seq !== searchSeq.current) return;
    setUnavailable(why);
    setOptions(found);
  };

  // Which load is current: a response for a person no longer selected is
  // dropped rather than shown in the next person's section.
  const loadRef = useRef(0);

  const getDiagnoses = () => {
    const id = proBandId ?? familyMemberId;
    if (!id || !user_id) return;
    const load = ++loadRef.current;

    setFetchedDiagnoses([]); // Reset diagnoses
    axios
      .get(
        APICONSTANTS.getDiagnosesById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          params: { isProband: proBandId ? true : false }
        }
      )
      .then((response) => {
        if (load !== loadRef.current) return;
        const { diagnoses = [] } = response?.data || {};
        // The list was reset to empty when the request went out, so anything
        // in it now arrived meanwhile (a legend drop) — the server read the
        // record before it existed. Keep it rather than replace it.
        setFetchedDiagnoses((prev) => [
          ...diagnoses,
          ...notYetIn(diagnoses, prev)
        ]);
      })
      .catch((err) => {
        console.log('ERRRR-', err);
      });
  };

  useEffect(() => {
    const handle = setTimeout(fetchDataByDisease, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line
  }, [searchTerm]);

  useEffect(() => {
    setExpandedNewIds(new Set());
    getDiagnoses();
    // eslint-disable-next-line
  }, [familyMemberId, proBandId, clinicalRefreshKey]);

  const isGeneId = (id) => id?.startsWith('NCBIGene:');
  const isPhenotypeId = (id) => id?.startsWith('HP:') && id !== 'HP:999999';

  useEffect(() => {
    const memberData = proBandId
      ? users?.selectedUser
      : (users?.family || []).find((m) => m.familyMemberId === familyMemberId);
    const incoming = (memberData?.diagnoses || []).filter(
      (d) => !isGeneId(d.id) && !isPhenotypeId(d.id)
    );
    if (!incoming.length) return;
    setFetchedDiagnoses((prev) => {
      const prevIds = new Set(prev.map((d) => d.id));
      if (incoming.every((d) => prevIds.has(d.id))) return prev;
      return incoming;
    });
  }, [users?.selectedUser?.diagnoses, users?.family]);

  // A disease dragged onto this person from the legend. See useRowRecordSync.
  useRowRecordSync({
    memberRecords,
    pid: data?.pid,
    field: 'diagnoses',
    keep: isDiseaseRecord,
    shownIds: fetchedDiagnoses.map((d) => d.id),
    onGained: (added) =>
      setFetchedDiagnoses((prev) => [...prev, ...notYetIn(prev, added)])
  });

  // The next records, or a function of the current ones. Anything that runs
  // after an await passes a FUNCTION: the records captured before it are stale
  // by then, and writing them back reverted whatever landed in between — a
  // legend drop, or another section's write — and then saved the reversion.
  const updateData = (next) => {
    setMemberRecords(typeof next === 'function' ? next : [...next]);

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));
  };

  const onSave = async (itemsOverride) => {
    const items = itemsOverride || selectedItems;
    const id = proBandId ?? familyMemberId;

    if (!id || !user_id)
      return setSnack({
        status: true,
        msg: t('Kindly save pedigree first!'),
        severity: 'error'
      });

    if (items?.length === 0) {
      return setSnack({
        status: true,
        msg: t(
          'No diseases selected. Please add at least one disease to proceed.'
        ),
        severity: 'error'
      });
    }

    const geneItems = items.filter((item) => isGeneId(item.id));
    const phenotypeItems = items.filter((item) => isPhenotypeId(item.id));
    const diseaseItems = items.filter(
      (item) => !isGeneId(item.id) && !isPhenotypeId(item.id)
    );

    const newDiseaseIds = new Set(diseaseItems.map((d) => d.id));
    setExpandedNewIds((prev) => new Set([...prev, ...newDiseaseIds]));

    setLoading(true);

    try {
      // Each successful write adds a patch for THIS person, applied at the end
      // to the records as they are THEN, not as they were before the awaits
      // (see updateData), and merged rather than copied (mergeSavedRecords).
      const patches = [];
      const patchPerson = (field, saved, items) =>
        patches.push((elem) =>
          elem.pid === data.pid
            ? {
                ...elem,
                [field]: mergeSavedRecords(
                  elem[field],
                  saved,
                  items.map((item) => item.id)
                )
              }
            : elem
        );

      if (diseaseItems.length > 0) {
        const res = await axios.post(APICONSTANTS.diagnoses, {
          familyId: user.familyId,
          familyMemberId: id,
          user_id: user_id,
          isProband: proBandId ? true : false,
          diagnoses: diseaseItems
        });
        if (res.data?.success && res.data?.allDiagnoses) {
          setFetchedDiagnoses(res.data.allDiagnoses);
          dispatchUpdate({
            type: EVENTS.SET_PEDIGREE_DIAGNOSES,
            value: res.data.diagnoses
          });
          patchPerson('diagnoses', res.data.allDiagnoses, diseaseItems);
          setLabelsSet((prev) => ({
            ...prev,
            diseasesMap: {
              ...prev.diseasesMap,
              ...diseaseItems.reduce((acc, d) => {
                if (!(d.id in prev.diseasesMap)) acc[d.id] = true;
                return acc;
              }, {})
            }
          }));
        }
      }

      if (geneItems.length > 0) {
        const res = await axios.post(APICONSTANTS.genotypes, {
          familyId: user.familyId,
          familyMemberId: id,
          user_id: user_id,
          isProband: proBandId ? true : false,
          genotypes: geneItems.map((g) => ({ id: g.id, name: g.name }))
        });
        if (res.data?.success) {
          dispatchUpdate({
            type: EVENTS.SET_PEDIGREE_GENOTYPES,
            value: res.data.genotypes
          });
          patchPerson('genotypes', res.data.genotypes, geneItems);
        }
      }

      if (phenotypeItems.length > 0) {
        const res = await axios.post(APICONSTANTS.phenotypes, {
          familyId: user.familyId,
          familyMemberId: id,
          user_id: user_id,
          isProband: proBandId ? true : false,
          phenotypes: phenotypeItems.map((p) => ({ id: p.id, name: p.name }))
        });
        if (res.data?.success) {
          dispatchUpdate({
            type: EVENTS.SET_PEDIGREE_PHENOTYPES,
            value: res.data.phenotypes
          });
          patchPerson('phenotypes', res.data.phenotypes, phenotypeItems);
        }
      }

      updateData((prev) =>
        patches.reduce((records, patch) => records.map(patch), prev)
      );
      setSearchTerm('');
      setSelectedItems([]);
      setSnack({
        status: true,
        msg: t('Saved successfully'),
        severity: 'success'
      });
    } catch (err) {
      setSnack({
        status: true,
        msg: err.response?.data?.error ?? t('An error has occurred!'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (event, value) => {
    if (!value) return;

    const immediate = [];
    let chooser = null;

    value.forEach((newItem) => {
      // Guard: the same disease a second time, under any of its codes or by
      // name, would put two records of it on one person (and desync pedigree
      // labels and exports downstream). Every code of the row is compared, so
      // ORPHA:908 is caught on top of OMIM:300624 (TGCLOUD-1892).
      const present = recordedAs(newItem, [
        ...fetchedDiagnoses,
        ...selectedItems,
        ...immediate
      ]);
      if (present) {
        setSnack({
          status: true,
          msg: t("'{{name}}' is already recorded for this person.", {
            name: present.name || newItem.name
          }),
          severity: 'info'
        });
        return;
      }

      // More than one code for this disease → let the clinician choose which
      // to store instead of silently picking one.
      if (newItem.codingSystems && newItem.codingSystems.length > 1) {
        if (!chooser) chooser = newItem;
      } else {
        immediate.push(storedDiseaseFor(newItem, newItem.id));
      }
    });

    if (immediate.length > 0) onSave(immediate);
    if (chooser) {
      setCodingChoice({
        open: true,
        option: chooser,
        selectedId: chooser.codingSystems[0].id
      });
    }
    setSearchTerm('');
  };

  const closeCodingChoice = () =>
    setCodingChoice({ open: false, option: null, selectedId: null });

  const handleCodingChoiceConfirm = () => {
    const { option, selectedId } = codingChoice;
    closeCodingChoice();
    // The chosen code with its own name: Q99.2 is "Fragile X chromosome".
    if (option && selectedId) {
      return onSave([storedDiseaseFor(option, selectedId)]);
    }
    return Promise.resolve();
  };

  // Keep saveRef current so parent can trigger save with latest state
  useEffect(() => {
    if (saveRef) saveRef.current = onSave;
  });

  // Closing the disease editor is two functions, not one. TGCLOUD-1823.
  //
  // `closeDiagnoseModalNow` is the real close and is what the post-save and
  // post-delete paths call — those must never be second-guessed, the record is
  // already written. `handleModalClose` is the guarded one wired to the dialog's
  // header X and the child's Cancel button, the only two user-initiated close
  // paths (CustomDialog is given no `allowBackgroundClose` here, so backdrop
  // and Escape do nothing today).
  const closeDiagnoseModalNow = () => {
    setDiagnoseDirty(false);
    setUnsavedPrompt(false);
    setOpenModal(false);
  };

  const handleModalClose = () => {
    if (diagnoseDirty) {
      setUnsavedPrompt(true);
      return;
    }
    closeDiagnoseModalNow();
  };

  const onDiagnoseClick = (diagnose) => {
    setSelectedDiagnose(diagnose);
    setDiagnoseDirty(false);
    setOpenModal(true);
  };

  const diseaseSearchRef = React.useRef(null);
  useRecordOpenRequest({
    event: 'open-disease-record',
    items: fetchedDiagnoses,
    onOpen: onDiagnoseClick,
    searchRef: diseaseSearchRef
  });

  const onEditDiagnose = (diagnose, ageConfirmation = false) => {
    const id = proBandId ?? familyMemberId;
    if (!id || !user_id) return;

    if (
      diagnose &&
      diagnose.age !== undefined &&
      diagnose.age !== null &&
      diagnose.age !== ''
    ) {
      const personAge = getPersonAge(data);
      const ageNum = Number(diagnose.age);
      if (personAge !== null && ageNum > personAge) {
        setSnack({
          status: true,
          msg: t(
            "Age at diagnosis cannot be greater than the person's age ({{personAge}}).",
            { personAge }
          ),
          severity: 'error'
        });
        return;
      }
    }

    if (diagnose.updatedDate) {
      diagnose.dateReported = diagnose.updatedDate;
    }

    return axios
      .put(
        APICONSTANTS.getDiagnosesById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          diagnose,
          isProband: proBandId ? true : false,
          ageConfirmation
        }
      )
      .then((res) => {
        const diagnosesData = res?.data?.data || {};
        const {
          probandDiagnoses = {},
          familyDiagnoses = [],
          updatedEntity
        } = diagnosesData;

        setFetchedDiagnoses(updatedEntity?.diagnoses || []);

        // A colour/alias edit fans out server-side to every family member with
        // the same disease (updateAliasAndColor), so refresh ALL members — not
        // just the edited one — or the other nodes keep their stale colour until
        // a reload. Mirror the delete path: sync the sidebar/legend via the
        // reducer AND rebuild memberRecords, which is what the canvas node fills
        // render from (updating pedigreeSelectedUsers alone does not repaint them).
        dispatchUpdate({
          type: EVENTS.UPDATE_EACH_PEDIGREE_DIAGNOSES,
          value: {
            probandDiagnoses,
            familyDiagnoses
          }
        });

        const diagnosesByPid = new Map(
          (familyDiagnoses || []).map((m) => [m.pid, m.diagnoses])
        );
        if (probandDiagnoses?.pid) {
          diagnosesByPid.set(probandDiagnoses.pid, probandDiagnoses.diagnoses);
        }

        // Functional: this runs after the request, when the records captured
        // before it may be stale (see updateData).
        updateData((prev) =>
          prev.map((elem) =>
            diagnosesByPid.has(elem.pid)
              ? { ...elem, diagnoses: diagnosesByPid.get(elem.pid) }
              : elem
          )
        );

        setSnack({
          status: true,
          msg: t('Updated successfully'),
          severity: 'success'
        });
        closeDiagnoseModalNow();
      })
      .catch((err) => {
        if (err.response?.data?.ageConfirmation?.status) {
          const { dateReported } = err.response.data.ageConfirmation.diagnose;
          setAgeConfirmationModal({
            data: {
              ...diagnose,
              dateReported: dateReported,
              updatedDate: diagnose.dateReported
            },
            status: true
          });
        } else {
          setSnack({
            status: true,
            msg: err.response?.data?.message ?? t('An error has occurred!'),
            severity: 'error'
          });
        }
      });
  };

  const onDeleteDiagnose = async (diagnoseId) => {
    const id = proBandId ?? familyMemberId;
    if (!id || !user_id) return;

    return axios
      .delete(
        APICONSTANTS.deleteDiagnosesById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          data: {
            diagnoseId,
            isProband: proBandId ? true : false
          }
        }
      )
      .then((res) => {
        const diagnosesData = res?.data?.data || [];
        const { probandDiagnoses = {}, familyDiagnoses = [] } = diagnosesData;

        dispatchUpdate({
          type: EVENTS.UPDATE_EACH_PEDIGREE_DIAGNOSES,
          value: {
            probandDiagnoses,
            familyDiagnoses
          }
        });

        // Functional: this runs after the request, when the records captured
        // before it may be stale (see updateData).
        updateData((prev) =>
          prev.map((elem) => {
            if (elem.pid === data.pid) {
              return {
                ...elem,
                diagnoses: elem.diagnoses.filter(
                  (diagnose) => diagnose._id !== diagnoseId
                )
              };
            }
            return elem;
          })
        );

        setFetchedDiagnoses(
          fetchedDiagnoses.filter((diagnose) => diagnose._id !== diagnoseId)
        );
        setSnack({
          status: true,
          msg: t('Diagnoses deleted successfully'),
          severity: 'success'
        });
        closeDiagnoseModalNow();
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: err.response?.data?.message ?? t('An error has occurred!'),
          severity: 'error'
        });
      });
  };

  const confirmAgeHandler = async () => {
    const ageConfirmation = true;
    return onEditDiagnose(ageConfirmationModal.data, ageConfirmation).then(() =>
      setAgeConfirmationModal({ status: false })
    );
  };

  const personSummary = formatPersonSummary(data);

  const isSomeClinicallyConfirmed = fetchedDiagnoses.some(
    (diagnose) => diagnose.clinicallyConfirmed
  );

  return (
    <>
      <Grid container spacing={2}>
        {unavailable && (
          <Grid size={12}>
            <Alert severity="warning">
              {t(unavailable.message)}
              {unavailable.sources
                ? ` (${unavailable.sources.join(', ')})`
                : ''}
            </Alert>
          </Grid>
        )}

        <Grid size={12}>
          <Autocomplete
            multiple
            clearOnBlur={false}
            id="tags-outlined"
            options={options}
            value={[]}
            getOptionLabel={(option) => option.name}
            onChange={handleSelect}
            disabled={isDisabled}
            filterOptions={(options, state) => options}
            // filterSelectedOptions
            // The shared row (TGCLOUD-1892): the code that will be stored and
            // its vocabulary, a chip per code on a merged row, the Mondo
            // concept, what the words matched, and the bridge and phenotype
            // labels this picker used to drop.
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <DiagnosisOptionRow option={option} chooser />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('Search for Diseases')}
                data-va-anchor="pedigree.diagnoses.search"
                inputRef={diseaseSearchRef}
                placeholder={t('Search...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
            slotProps={{
              listbox: {
                sx: {
                  maxHeight: '320px',
                  overflowY: 'auto'
                }
              }
            }}
          />
        </Grid>
        <Grid spacing={2} size={12}>
          {[
            ...fetchedDiagnoses,
            ...selectedItems.filter(
              (s) => !fetchedDiagnoses.some((f) => f.id === s.id)
            )
          ].map((diagnose) => {
            const isSaved = fetchedDiagnoses.some((f) => f.id === diagnose.id);
            const isExpanded = isSaved && expandedNewIds.has(diagnose.id);
            return (
              <React.Fragment key={diagnose._id || diagnose.id}>
                <CardGrid
                  item
                  xs={12}
                  onClick={() =>
                    isSaved && !isExpanded && onDiagnoseClick(diagnose)
                  }
                  style={{
                    cursor: isSaved && !isExpanded ? 'pointer' : 'default',
                    ...(isExpanded
                      ? {
                          borderBottom: 'none',
                          borderRadius: '5px 5px 0 0',
                          marginBottom: 0
                        }
                      : {}),
                    ...(isSaved
                      ? {}
                      : {
                          backgroundColor: '#FFF3E0',
                          color: '#BA7517',
                          border: '1px solid #BA7517'
                        })
                  }}
                >
                  <Grid size={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Tooltip title={diagnose.name} arrow>
                        <DiagnoseName
                          sx={{
                            fontWeight: 600,
                            color: TILE_COLOURS.disease.text
                          }}
                        >
                          {diagnose.name}
                        </DiagnoseName>
                      </Tooltip>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        {diagnose.comment && (
                          <Tooltip
                            title={
                              <Box
                                sx={{ fontSize: 12, whiteSpace: 'pre-wrap' }}
                              >
                                {diagnose.comment}
                              </Box>
                            }
                          >
                            <StickyNote2OutlinedIcon
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                color: TILE_COLOURS.disease.accent,
                                fontSize: 18,
                                cursor: 'help'
                              }}
                            />
                          </Tooltip>
                        )}
                        {diagnose.clinicallyConfirmed && <RightIcon />}
                      </Box>
                    </Box>
                    {diagnose.id && (
                      <Typography
                        variant="body2"
                        sx={{ color: TILE_COLOURS.disease.subtext }}
                      >
                        {diagnose.id}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: !diagnose.age
                          ? 'orange'
                          : TILE_COLOURS.disease.subtext
                      }}
                    >
                      {diagnose.age
                        ? t('Age: {{age}}', { age: diagnose.age })
                        : t('Age*')}
                    </Typography>
                  </Grid>
                </CardGrid>
                {isSaved && (
                  <Collapse in={isExpanded} unmountOnExit>
                    <InlineDiagnosisCapture
                      record={diagnose}
                      patientDob={data?.dob}
                      maxAge={getPersonAge(data)}
                      dateFormat={theme.dateFormat}
                      anonymous={anonymous}
                      onSaveCapture={(update) => onEditDiagnose(update)}
                      onSkip={() => {
                        setExpandedNewIds((prev) => {
                          const next = new Set(prev);
                          next.delete(diagnose.id);
                          return next;
                        });
                      }}
                    />
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </Grid>

        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            {isSomeClinicallyConfirmed ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <RightIcon />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 'bold'
                  }}
                >
                  {t('Clinically Confirmed')}
                </Typography>
              </Box>
            ) : (
              <span />
            )}
            <IconButton
              size="small"
              onClick={() =>
                modificationHistoryHandle(CONSTANTS.auditLogType['diagnoses'])
              }
            >
              <HistoryIcon />
            </IconButton>
          </Box>
        </Grid>

        <UnsavedChangesDialog
          open={unsavedPrompt}
          onCancel={() => setUnsavedPrompt(false)}
          onDiscard={closeDiagnoseModalNow}
        />
        {openModal && (
          <CustomDialog
            title={
              <ModalHeader
                title={t('Edit Disease')}
                personSummary={personSummary}
              />
            }
            open={openModal}
            handleClose={handleModalClose}
            minHeight="auto"
            maxWidth={CLINICAL_RECORD_DIALOG_WIDTH}
            titleStyle={{ marginRight: '40px' }}
            footerAction={
              selectedDiagnose?.id && modificationHistoryHandle ? (
                <IconButton
                  size="small"
                  title={t('Record history')}
                  onClick={() =>
                    modificationHistoryHandle(
                      CONSTANTS.auditLogType.diagnoses,
                      {},
                      // the element's stable string id (== backend entityId),
                      // NOT the mongoose _id
                      selectedDiagnose.id
                    )
                  }
                >
                  <HistoryIcon />
                </IconButton>
              ) : null
            }
          >
            <EditDiagnoseModal
              formValues={selectedDiagnose}
              onSubmit={onEditDiagnose}
              onDeleteDiagnose={onDeleteDiagnose}
              isDisabled={isDisabled}
              genotypes={data?.genotypes}
              patientDob={data?.dob}
              currentUserName={currentUserName}
              staffMemberNames={staffMemberNames}
              // Lets the modal refuse a re-code onto a code this person
              // already carries on another record.
              siblingDiagnoses={fetchedDiagnoses}
              handleClose={handleModalClose}
              onDirty={() => setDiagnoseDirty(true)}
            />
          </CustomDialog>
        )}
        {codingChoice.open && (
          <CustomDialog
            title={'Choose coding system'}
            open={codingChoice.open}
            handleClose={closeCodingChoice}
            minHeight="auto"
            showActions={true}
            saveBtnLabel={'Add'}
            isParentHandlingLoading={true}
            onSave={handleCodingChoiceConfirm}
          >
            <DiseaseCodeChoice
              option={codingChoice.option}
              selectedId={codingChoice.selectedId}
              onChange={(selectedId) =>
                setCodingChoice((prev) => ({ ...prev, selectedId }))
              }
            />
          </CustomDialog>
        )}
        {ageConfirmationModal.status && (
          <CustomDialog
            title={'Confirm diagnose age'}
            open={ageConfirmationModal.status}
            handleClose={() => setAgeConfirmationModal({ status: false })}
            minHeight="auto"
            showActions={true}
            saveBtnLabel={'Save'}
            onSave={confirmAgeHandler}
          >
            <DeleteModal
              content={
                <>
                  <p>{t('Are you sure you want to continue?')}</p>
                  <p>
                    {t('You have already added diagnoses for')}{' '}
                    <strong>{`${ageConfirmationModal.data.name} `} </strong>
                    {t('with age')}{' '}
                    <strong>{`${ageConfirmationModal.data.age} `}</strong>
                    {t('reported on')}{' '}
                    <strong>
                      {dayjs(ageConfirmationModal.data.dateReported).format(
                        'DD/MM/YYYY'
                      )}
                    </strong>
                    .
                  </p>
                </>
              }
            />
          </CustomDialog>
        )}
      </Grid>
    </>
  );
};

export default DiagnosesMenu;
